import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * DateRangeControl — Sprint 3.7.
 *
 * Preset buttons (Today / Yesterday / 7d / 30d / MTD / YTD) plus two
 * <input type="date"> fields for custom ranges. Emits ISO YYYY-MM-DD.
 */

const PRESETS = [
    { key: 'today',     label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7',     label: 'Last 7d' },
    { key: 'last30',    label: 'Last 30d' },
    { key: 'mtd',       label: 'MTD' },
    { key: 'qtd',       label: 'QTD' },
    { key: 'ytd',       label: 'YTD' },
];

export default function DateRangeControl({ range, onChange }) {
    const { t } = useTranslation();
    const activePreset = useMemo(() => detectPreset(range), [range]);

    const apply = (key) => {
        const r = computePreset(key);
        if (r) onChange(r);
    };

    return (
        <div>
            <div className="v3-anlx-presets" role="group" aria-label={t('Date range presets')}>
                {PRESETS.map(p => (
                    <button
                        key={p.key}
                        type="button"
                        className={`v3-anlx-preset${activePreset === p.key ? ' is-active' : ''}`}
                        onClick={() => apply(p.key)}
                    >{t(p.label)}</button>
                ))}
            </div>
            <div className="v3-anlx-date-inputs">
                <input
                    type="date"
                    className="v3-anlx-date-input"
                    value={range?.from || ''}
                    max={range?.to || undefined}
                    onChange={(e) => onChange({ from: e.target.value, to: range?.to })}
                    aria-label={t('Start date')}
                />
                <input
                    type="date"
                    className="v3-anlx-date-input"
                    value={range?.to || ''}
                    min={range?.from || undefined}
                    onChange={(e) => onChange({ from: range?.from, to: e.target.value })}
                    aria-label={t('End date')}
                />
            </div>
        </div>
    );
}

function fmt(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

export function computePreset(key) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (key === 'today')     return { from: fmt(today), to: fmt(today) };
    if (key === 'yesterday') {
        const y = new Date(today); y.setDate(today.getDate() - 1);
        return { from: fmt(y), to: fmt(y) };
    }
    if (key === 'last7') {
        const f = new Date(today); f.setDate(today.getDate() - 6);
        return { from: fmt(f), to: fmt(today) };
    }
    if (key === 'last30') {
        const f = new Date(today); f.setDate(today.getDate() - 29);
        return { from: fmt(f), to: fmt(today) };
    }
    if (key === 'mtd') {
        const f = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: fmt(f), to: fmt(today) };
    }
    if (key === 'qtd') {
        const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
        const f = new Date(today.getFullYear(), quarterStartMonth, 1);
        return { from: fmt(f), to: fmt(today) };
    }
    if (key === 'ytd') {
        const f = new Date(today.getFullYear(), 0, 1);
        return { from: fmt(f), to: fmt(today) };
    }
    return null;
}

function detectPreset(range) {
    if (!range || !range.from || !range.to) return null;
    for (const p of PRESETS) {
        const r = computePreset(p.key);
        if (r && r.from === range.from && r.to === range.to) return p.key;
    }
    return null;
}
